import { CheckCircle2, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import type { AxiosInstance } from "axios";

import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { cn } from "../lib/cn.js";
import { apiErrorMessage } from "../lib/api.js";
import { evaluatePassword, MIN_PASSWORD_LENGTH } from "../lib/password.js";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter.js";
import { Logo } from "../components/Logo.js";

/**
 * Redeem an invite or a reset link.
 *
 * IT DOES NOT SIGN THEM IN, and that mirrors the API deliberately: setting a password
 * revokes every existing session, so handing back a fresh one in the same breath would
 * make the flow useless for "get everyone out". They set it, then sign in.
 *
 * THE REQUIREMENTS ARE SHOWN AND CHECKED LIVE, but the server is still the authority — the
 * client checks are a courtesy that spares a round-trip, never the gate. The one real rule
 * is length: the policy is deliberately length-only (NIST-style, no "one uppercase, one
 * digit" theatre that pushes people toward `Password1!`). The confirm field is not a server
 * rule at all — it catches a typo in a value the user cannot see and cannot recover without
 * a new link.
 */


export function SetPasswordScreen({
  client,
  token,
}: {
  readonly client: AxiosInstance;
  readonly token: string;
}): React.JSX.Element {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const check = useMemo(() => evaluatePassword(password, confirm), [password, confirm]);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!check.valid) return;
    setBusy(true);
    setError(null);
    try {
      await client.post("/auth/password/set", { token, password });
      setDone(true);
    } catch (caught) {
      setError(apiErrorMessage(caught, "This link is no longer valid."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      {done ? (
        <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-6 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="size-8" />
          </span>
          <div>
            <h2 className="text-2xl font-bold">Password set</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Your password is saved and any old sessions were signed out. You can sign in now.
            </p>
          </div>
          <Button asChild className="h-11 w-full">
            <a href="/">Go to sign in</a>
          </Button>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 text-center">
            <span className="bg-primary/10 text-primary mx-auto mb-4 flex size-12 items-center justify-center rounded-xl">
              <ShieldCheck className="size-6" />
            </span>
            <h2 className="text-3xl font-bold">Choose a password</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Set the password for your account. Make it long — length is what keeps it safe.
            </p>
          </div>

          <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
            <div className="grid gap-1.5">
              <Label htmlFor="new-password">New password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={reveal ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••••••"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  className="h-11 pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {/* `type="button"` is load-bearing: a bare button inside a form submits. */}
                <button
                  type="button"
                  onClick={() => setReveal((current) => !current)}
                  aria-label={reveal ? "Hide password" : "Show password"}
                  aria-pressed={reveal}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 rounded-md p-1 transition-colors"
                >
                  {reveal ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
              </div>

              <PasswordStrengthMeter check={check} shown={password.length > 0} />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type={reveal ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••••••"
                required
                className={cn("h-11", confirm.length > 0 && !check.matches && "border-destructive")}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {confirm.length > 0 && !check.matches && (
                <p className="text-destructive text-xs">Passwords do not match yet.</p>
              )}
            </div>

            <ul className="flex flex-col gap-1.5 text-sm" aria-label="Password requirements">
              <Requirement met={check.longEnough}>At least {MIN_PASSWORD_LENGTH} characters</Requirement>
              <Requirement met={check.matches}>Both entries match</Requirement>
            </ul>

            {error !== null && (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" disabled={busy || !check.valid} className="h-11 w-full">
              {busy && <Loader2 className="animate-spin" />}
              {busy ? "Saving…" : "Set password"}
            </Button>
          </form>
        </div>
      )}
    </Shell>
  );
}

/** The shared branded frame — the split card, brand blobs and wordmark from the login screen. */
function Shell({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-(--page) px-4 py-10">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-amber-400 opacity-20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-blue-500 opacity-20 blur-3xl" />
      </div>

      <div className="absolute top-8 left-8 z-10">
        <Logo className="h-7 w-auto" />
      </div>

      <div className="bg-card relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl border shadow-xl md:flex-row">
        {/* The pitch. Hidden below md so the form is never pushed under the fold on a phone. */}
        <div className="hidden flex-col justify-center gap-6 bg-gradient-to-b from-blue-700 to-indigo-800 p-10 text-center text-white md:flex md:w-1/2">
          <h1 className="text-4xl leading-tight font-extrabold">You&apos;re almost in</h1>
          <p className="text-lg text-blue-100">
            One password and your workspace is ready — reports, campaigns and attribution you can check.
          </p>
          <div className="flex justify-center">
            <span className="rounded-full bg-white/20 px-6 py-2 text-sm font-medium backdrop-blur-sm">
              Secured with modern password hashing
            </span>
          </div>
        </div>

        <div className="w-full p-8 md:w-1/2 md:p-10">{children}</div>
      </div>
    </main>
  );
}

function Requirement({ met, children }: { readonly met: boolean; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <li className={cn("flex items-center gap-2", met ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
      <CheckCircle2 className={cn("size-4 shrink-0", met ? "opacity-100" : "opacity-30")} />
      <span>{children}</span>
    </li>
  );
}
